import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";

import { colors } from "#app/theme/v1/colors";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { QuoteRequestLine } from "#modules/quoteRequestLines/QuoteRequestLine";
import { useReorderQuoteRequestLines } from "#modules/quoteRequestLines/view/useReorderQuoteRequestLines";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";
import { Checkbox } from "#shared/view/designSystem/Checkbox";
import { AssemblyWithChildren } from "shared";

import { useAssembliesContext } from "../context/AssembliesContext";
import { SortProperty } from "./useQuoteRequestLinesSorting";

type RowProps = {
  batchKey: string;
  assembly: AssemblyWithChildren<QuoteRequestLineDto>;
  showCheckbox: boolean;
  checked: boolean;
  onToggle: () => void;
  display?: "batch" | "individual";
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  isCustomInputsBulkLoading: boolean;
};

const Row = ({
  batchKey,
  assembly,
  showCheckbox,
  checked,
  onToggle,
  assembliesInBatch,
  isCustomInputsBulkLoading,
}: RowProps) => {
  const { quoteRequestLine, children } = assembly;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: batchKey,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    position: "relative",
    zIndex: isDragging ? 1 : "auto",
  };

  return (
    <Group ref={setNodeRef} style={style} align="flex-start" wrap="nowrap" gap="xxs">
      <Flex align="flex-start" gap="xxs" w="100%">
        <Stack gap="xs" pt="xs" align="center">
          <span
            {...attributes}
            {...listeners}
            style={{
              cursor: "grab",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Icon name="gripVertical" size={12} color="gray" />
          </span>
          {showCheckbox && (
            <Checkbox
              size="xs"
              mr={2}
              color={colors.blue[4]}
              checked={checked}
              onChange={onToggle}
              styles={{ input: { cursor: "pointer" } }}
            />
          )}
        </Stack>

        <QuoteRequestLine
          assemblyChildren={children}
          quoteRequestLineId={quoteRequestLine.id}
          isParent
          parentQuoteRequestLineId={quoteRequestLine.id}
          assembliesInBatch={assembliesInBatch}
          isCustomInputsBulkLoading={isCustomInputsBulkLoading}
        />
      </Flex>
    </Group>
  );
};

type Props = {
  batches: Map<string, AssemblyWithChildren<QuoteRequestLineDto>[]>;
  sortProperty: SortProperty;
  isCustomInputsBulkLoading: boolean;
};

export const QuoteRequestLines = ({ batches, sortProperty, isCustomInputsBulkLoading }: Props) => {
  const { selectedQuoteRequestLines, setSelectedQuoteRequestLines, activeAssembliesByBatch } =
    useAssembliesContext();

  const initialBatchEntries = useMemo(
    () => Array.from(batches.entries()).filter(([, arr]) => arr.length > 0),
    [batches],
  );

  const [batchKeys, setBatchKeys] = useState<string[]>(() =>
    initialBatchEntries.map(([key]) => key),
  );
  const [activeSortProperty, setActiveSortProperty] = useState<SortProperty>(sortProperty);

  const batchMap = useMemo(() => new Map(initialBatchEntries), [initialBatchEntries]);

  const orderedBatchEntries = useMemo(
    () =>
      batchKeys
        .map((key) => {
          const arr = batchMap.get(key);
          return arr ? ([key, arr] as [string, AssemblyWithChildren<QuoteRequestLineDto>[]]) : null;
        })
        .filter(
          (entry): entry is [string, AssemblyWithChildren<QuoteRequestLineDto>[]] => entry !== null,
        ),
    [batchKeys, batchMap],
  );

  const quoteRequestId = useMemo(() => {
    const firstBatch = initialBatchEntries[0];
    return firstBatch?.[1]?.[0]?.quoteRequestLine.quoteRequestId ?? undefined;
  }, [initialBatchEntries]);

  const { reorderQuoteRequestLinesAsync } = useReorderQuoteRequestLines({
    quoteRequestId: quoteRequestId ?? undefined,
  });

  const selectedIdsSet = useMemo(
    () => new Set(selectedQuoteRequestLines.map((qrl) => qrl.id)),
    [selectedQuoteRequestLines],
  );

  const isSelected = useCallback((id: string) => selectedIdsSet.has(id), [selectedIdsSet]);

  const toggleBatch = useCallback(
    (batchAssemblies: AssemblyWithChildren<QuoteRequestLineDto>[]) => () => {
      const batchIds = batchAssemblies.map((a) => a.quoteRequestLine.id);
      setSelectedQuoteRequestLines((prev) => {
        const allSelected = batchIds.every((id) => prev.some((qrl) => qrl.id === id));

        return allSelected
          ? prev.filter((qrl) => !batchIds.some((id) => qrl.id === id))
          : [
              ...prev,
              ...batchAssemblies
                .filter((qrl) => !prev.some((qrl2) => qrl2.id === qrl.quoteRequestLine.id))
                .map((qrl) => qrl.quoteRequestLine),
            ];
      });
    },
    [setSelectedQuoteRequestLines],
  );

  const dataIds = useMemo<UniqueIdentifier[]>(
    () => orderedBatchEntries.map(([key]) => key),
    [orderedBatchEntries],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = dataIds.indexOf(active.id);
        const newIndex = dataIds.indexOf(over.id);
        const reorderedKeys = arrayMove([...batchKeys], oldIndex, newIndex);
        setBatchKeys(reorderedKeys);

        if (!quoteRequestId) {
          return;
        }

        const orderedIds = reorderedKeys.flatMap((key) => {
          const batchAssemblies = batchMap.get(key);
          return batchAssemblies
            ? batchAssemblies.map((assembly) => assembly.quoteRequestLine.id)
            : [];
        });

        await reorderQuoteRequestLinesAsync({ quoteRequestId, orderedIds });
      }
    },
    [dataIds, batchKeys, batchMap, quoteRequestId, reorderQuoteRequestLinesAsync],
  );

  useEffect(() => {
    const newKeys = initialBatchEntries.map(([key]) => key);
    const sortPropertyChanged = sortProperty !== activeSortProperty;
    if (sortPropertyChanged) {
      setActiveSortProperty(sortProperty);
      setBatchKeys(newKeys);
    } else if (sortProperty !== "index") {
      setBatchKeys((prev) => {
        const isSameOrder =
          prev.length === newKeys.length && prev.every((key, i) => key === newKeys[i]);
        return isSameOrder ? prev : newKeys;
      });
    } else {
      setBatchKeys((prev) => {
        const isCompleteUserOrder =
          prev.length === newKeys.length && newKeys.every((key) => prev.includes(key));
        return isCompleteUserOrder ? prev : newKeys;
      });
    }
  }, [initialBatchEntries, sortProperty, activeSortProperty]);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
        {orderedBatchEntries.map(([key, batchAssemblies]) => {
          const firstAssembly = batchAssemblies[0];

          if (!firstAssembly) {
            return null;
          }

          const activeAssemblyId = activeAssembliesByBatch[firstAssembly.quoteRequestLine.partId];
          const activeAssembly =
            batchAssemblies.find((a) => a.quoteRequestLine.id === activeAssemblyId) ||
            firstAssembly;

          const batchIds = batchAssemblies.map((a) => a.quoteRequestLine.id);
          const allSelected = batchIds.every((id) => isSelected(id));

          return (
            <Row
              key={key}
              batchKey={key}
              assembly={activeAssembly}
              showCheckbox
              checked={allSelected}
              onToggle={toggleBatch(batchAssemblies)}
              assembliesInBatch={batchAssemblies}
              isCustomInputsBulkLoading={isCustomInputsBulkLoading}
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
};
